import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Shield, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Senior Section: game availability over WhatsApp + the captain's daily summary. */
export const SectionGameReminders = () => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState({});
  const [wording, setWording] = useState({});
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [settings, game, captain, squad] = await Promise.all([
        axios.get(`${API}/section/whatsapp/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/section-templates/game`, { withCredentials: true })
          .catch(() => ({ data: {} })),
        axios.get(`${API}/admin/whatsapp/section-templates/captain-summary`, { withCredentials: true })
          .catch(() => ({ data: {} })),
        axios.get(`${API}/admin/whatsapp/section-templates/squad`, { withCredentials: true })
          .catch(() => ({ data: {} })),
      ]);
      setData(settings.data);
      setTemplates({ game: game.data, "captain-summary": captain.data, squad: squad.data });
      setWording({
        game: game.data.body || "",
        "captain-summary": captain.data.body || "",
        squad: squad.data.body || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the Section settings");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/section/whatsapp/settings`, patch,
        { withCredentials: true });
      setData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  const registerTemplate = async (kind) => {
    setBusy(kind);
    try {
      const body = (wording[kind] || "").trim();
      const r = await axios.post(`${API}/admin/whatsapp/section-templates/${kind}`,
        body ? { body } : {}, { withCredentials: true });
      toast.success(`Submitted · status ${r.data.status || "pending"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register that template");
    } finally {
      setBusy("");
    }
  };

  const sendNow = async (what) => {
    setBusy(what);
    try {
      const r = await axios.post(`${API}/section/whatsapp/send-now`, { what },
        { withCredentials: true });
      toast.success(r.data.skipped
        ? `Nothing sent (${r.data.reason})`
        : `Sent ${r.data.sent} message(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setBusy("");
    }
  };

  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;

  const game = data.section_game_reminder || {};
  const captain = data.section_captain_summary || {};
  const enabledTeams = game.team_ids || [];
  const activeOnly = game.active_only_team_ids || [];

  const toggleTeam = (teamId) => {
    const next = enabledTeams.includes(teamId)
      ? enabledTeams.filter((t) => t !== teamId)
      : [...enabledTeams, teamId];
    save({ section_game_reminder: { ...game, team_ids: next } });
  };

  const toggleActiveOnly = (teamId) => {
    const next = activeOnly.includes(teamId)
      ? activeOnly.filter((t) => t !== teamId)
      : [...activeOnly, teamId];
    save({ section_game_reminder: { ...game, active_only_team_ids: next } });
  };

  const openMembers = async (team) => {
    setSheet({ team, loading: true, members: [] });
    try {
      const r = await axios.get(`${API}/section/whatsapp/team/${team.id}/members`,
        { withCredentials: true });
      setSheet({ team, loading: false, ...r.data });
    } catch {
      toast.error("Could not load that team sheet");
      setSheet(null);
    }
  };

  const saveMembers = async () => {
    const ids = sheet.members.filter((m) => m.available).map((m) => m.user_id);
    try {
      await axios.put(`${API}/section/whatsapp/team/${sheet.team.id}/members`,
        { user_ids: ids }, { withCredentials: true });
      toast.success(`${ids.length} player(s) available for selection`);
      setSheet(null);
      load();
    } catch {
      toast.error("Could not save that");
    }
  };

  const statusBadge = (tpl) => {
    const status = (tpl?.status || "not registered").toLowerCase();
    const tone = status === "approved" ? "bg-emerald-500/20 text-emerald-300"
      : status === "rejected" ? "bg-red-500/20 text-red-300"
        : "bg-amber-500/20 text-amber-300";
    return <Badge className={`text-[10px] ${tone}`}>{status}</Badge>;
  };

  return (
    <Card className="p-6 bg-slate-900/50" data-testid="section-game-reminders-card">
      <div className="flex items-start gap-2">
        <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-white">Section games (seniors)</h3>
          <p className="text-sm text-slate-400 mt-1">
            Asks the squad before a game — available for selection, not available, or joining
            for the F&amp;B. Available puts the player straight on the sign-up
            list — on standby if the squad is full, or in front of the captain if that team
            approves its own squad.
          </p>
        </div>
      </div>

      {!data.whatsapp_enabled && (
        <p className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="section-master-off">
          WhatsApp is switched off altogether in <strong>Credentials</strong>, so nothing below
          will send.
        </p>
      )}

      <div className="mt-5 space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-3">
          <div>
            <p className="text-sm font-semibold text-white">Game availability messages</p>
            <p className="text-xs text-slate-400">Sent when a game is added, then as a reminder</p>
          </div>
          <Switch checked={!!game.enabled} disabled={saving}
            onCheckedChange={(v) => save({ section_game_reminder: { ...game, enabled: v } })}
            data-testid="section-game-toggle" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-slate-400">Days before the game</label>
            <Input className="h-10 mt-1" defaultValue={(game.days_before || []).join(", ")}
              onBlur={(e) => save({
                section_game_reminder: {
                  ...game,
                  days_before: e.target.value.split(",").map((d) => parseInt(d.trim(), 10))
                    .filter((d) => !Number.isNaN(d)),
                },
              })}
              data-testid="section-days-before" />
            <p className="text-[11px] text-slate-500 mt-1">
              e.g. <strong>4, 2</strong> for a Friday game asks on Monday and again on
              Wednesday. Anyone who has already replied is left alone.
            </p>
          </div>
          <div>
            <label className="text-xs text-slate-400">Time of day</label>
            <Input className="h-10 mt-1" defaultValue={game.send_time || "18:00"}
              onBlur={(e) => save({
                section_game_reminder: { ...game, send_time: e.target.value },
              })}
              data-testid="section-send-time" />
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-white">Which teams</p>
          <p className="text-xs text-slate-400 mb-2">
            Nothing sends to a team until you tick it here. Start with one side to trial it.
          </p>
          <div className="space-y-1">
            {(data.teams || []).map((team) => {
              const reach = (data.reachable || {})[team.id] || {};
              const on = enabledTeams.includes(team.id);
              return (
                <div key={team.id}
                  className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2"
                  data-testid={`section-team-row-${team.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white">{team.name}</p>
                    <p className="text-[11px] text-slate-500">
                      {reach.with_phone || 0} of {reach.members || 0} have a mobile on file
                      {` · ${reach.active || 0} available for selection`}
                      {reach.ticked ? " (set by hand)" : " (from the last 2 months)"}
                      {team.captain_summary_time
                        ? ` · captain summary ${team.captain_summary_time}`
                        : ""}
                    </p>
                    {on && (
                      <div className="flex items-center gap-3 mt-1.5">
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <input type="checkbox" checked={activeOnly.includes(team.id)}
                            onChange={() => toggleActiveOnly(team.id)}
                            data-testid={`section-active-only-${team.id}`} />
                          Message only those available for selection
                        </label>
                        <button className="text-[11px] text-yellow-500 hover:underline"
                          onClick={() => openMembers(team)}
                          data-testid={`section-edit-members-${team.id}`}>
                          Choose who
                        </button>
                      </div>
                    )}
                  </div>
                  <Switch checked={on} disabled={saving}
                    onCheckedChange={() => toggleTeam(team.id)}
                    data-testid={`section-team-toggle-${team.id}`} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">Captain&apos;s daily summary</p>
              <p className="text-xs text-slate-400">
                Who&apos;s in, who&apos;s out, who hasn&apos;t replied — to the portal and WhatsApp,
                each day in the run-up to a game
              </p>
            </div>
            <Switch checked={!!captain.enabled} disabled={saving}
              onCheckedChange={(v) => save({
                section_captain_summary: { ...captain, enabled: v },
              })}
              data-testid="section-captain-toggle" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-slate-400">Start this many days out</label>
              <Input className="h-10 mt-1" defaultValue={captain.days_before ?? 3}
                onBlur={(e) => save({
                  section_captain_summary: {
                    ...captain, days_before: parseInt(e.target.value, 10) || 3,
                  },
                })}
                data-testid="section-captain-days" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Default time (captains can change theirs)</label>
              <Input className="h-10 mt-1" defaultValue={captain.send_time || "18:00"}
                onBlur={(e) => save({
                  section_captain_summary: { ...captain, send_time: e.target.value },
                })}
                data-testid="section-captain-time" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-2">
          <p className="text-sm font-semibold text-white">Templates</p>
          <p className="text-xs text-slate-400">
            WhatsApp only delivers approved templates outside a 24-hour reply window, so both of
            these need registering before anything sends. Edit the wording first — the
            placeholders stay as they are and get filled in per player
            ({"{{1}}"} name, {"{{2}}"} team, {"{{3}}"} opponent, {"{{4}}"} date,
            {" "}{"{{5}}"} kick off, {"{{6}}"} venue, {"{{7}}"} the day the squad is announced,
            which is worked out from the fixture date — Sunday for a Monday game).
          </p>
          {[["game", "Game availability"], ["captain-summary", "Captain summary"],
            ["squad", "Squad announcement"]].map(([kind, label]) => (
            <div key={kind} className="space-y-2 border-t border-slate-800 pt-3 first:border-0 first:pt-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-300">
                  {label} {statusBadge(templates[kind])}
                </span>
                <div className="flex items-center gap-2">
                  {templates[kind]?.default_body
                    && wording[kind] !== templates[kind].default_body && (
                    <Button size="sm" variant="ghost" className="h-8 text-[11px] text-slate-400"
                      onClick={() => setWording({
                        ...wording, [kind]: templates[kind].default_body,
                      })}
                      data-testid={`section-reset-${kind}`}>
                      Reset wording
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-[11px] border-slate-700"
                    disabled={busy === kind} onClick={() => registerTemplate(kind)}
                    data-testid={`section-register-${kind}`}>
                    {busy === kind ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                    Submit for approval
                  </Button>
                </div>
              </div>
              <Textarea rows={3} className="text-xs bg-slate-950/60 border-slate-800"
                value={wording[kind] ?? ""}
                placeholder="Message wording"
                onChange={(e) => setWording({ ...wording, [kind]: e.target.value })}
                data-testid={`section-wording-${kind}`} />
              {!!(templates[kind]?.buttons || []).length && (
                <div className="flex flex-wrap items-center gap-1"
                  data-testid={`section-buttons-${kind}`}>
                  <span className="text-[11px] text-slate-500 mr-1">Reply buttons:</span>
                  {templates[kind].buttons.map((b) => (
                    <Badge key={b.id} className="bg-slate-800 text-slate-200 text-[11px]">
                      {b.title}
                    </Badge>
                  ))}
                  <span className="text-[11px] text-amber-300/80 ml-1">
                    these only change on WhatsApp once you submit again and Meta approves
                  </span>
                </div>
              )}
              {templates[kind]?.status === "rejected" && templates[kind]?.rejection_reason && (
                <p className="text-[11px] text-red-300">
                  Meta said: {templates[kind].rejection_reason}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-9 text-[11px] border-slate-700"
            disabled={busy === "reminders"} onClick={() => sendNow("reminders")}
            data-testid="section-send-reminders">
            {busy === "reminders" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Send className="w-3 h-3 mr-1" />}
            Send game messages now
          </Button>
          <Button size="sm" variant="outline" className="h-9 text-[11px] border-slate-700"
            disabled={busy === "captain-summaries"} onClick={() => sendNow("captain-summaries")}
            data-testid="section-send-summaries">
            {busy === "captain-summaries" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Send className="w-3 h-3 mr-1" />}
            Send captain summaries now
          </Button>
        </div>
      </div>
      {sheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSheet(null)} data-testid="section-member-sheet">
          <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-5"
            onClick={(e) => e.stopPropagation()}>
            <h4 className="text-white font-semibold">
              {sheet.team?.name} — available for selection
            </h4>
            <p className="text-xs text-slate-400 mt-1 mb-3">
              Tick who is playing this season. Leave every box empty and the portal works it
              out from the last two months instead.
            </p>
            {sheet.loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
                {(sheet.members || []).map((m) => (
                  <label key={m.user_id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-800/60"
                    data-testid={`member-row-${m.user_id}`}>
                    <input type="checkbox" checked={!!m.available}
                      onChange={() => setSheet({
                        ...sheet,
                        members: sheet.members.map((x) => x.user_id === m.user_id
                          ? { ...x, available: !x.available } : x),
                      })}
                      data-testid={`member-tick-${m.user_id}`} />
                    <span className="text-sm text-slate-200 flex-1">{m.name}</span>
                    {!m.has_mobile && (
                      <span className="text-[10px] text-amber-400">no mobile</span>
                    )}
                    {m.opted_out && (
                      <span className="text-[10px] text-red-400">opted out</span>
                    )}
                    {m.muted && (
                      <span className="text-[10px] text-slate-400">muted this team</span>
                    )}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="ghost" className="text-slate-400"
                onClick={() => setSheet(null)}>Cancel</Button>
              <Button size="sm" className="bg-yellow-500 text-black hover:bg-yellow-400"
                onClick={saveMembers} data-testid="save-members-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
